"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ArrowDown, Cpu, Database, LockKeyhole, Pause, Play, Server } from "lucide-react";
import { STORY_LAYERS } from "@/app/concept/twin-story/story-geometry";
import { UkFlag } from "@/app/concept/groups/drawing-variant-regions";
import styles from "./research-visuals.module.css";

function TwinMark() {
  return <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" aria-hidden="true">
    {[12, 9, 6, 3].map(radius => <circle key={radius} cx="14" cy="14" r={radius} strokeWidth="1.3" />)}
  </svg>;
}

/** The original Twin particles, in the research palette, with two quiet motion studies. */
function TwinAnimation({ converge = false }: { converge?: boolean }) {
  const figure = useRef<HTMLElement>(null);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const element = figure.current;
    if (!element) return;
    let intersecting = false;
    const update = () => setVisible(intersecting && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => {
      intersecting = entry?.isIntersecting ?? false;
      update();
    });
    observer.observe(element);
    document.addEventListener("visibilitychange", update);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", update);
    };
  }, []);
  return <figure ref={figure} className={`${styles.animation} ${converge ? styles.converging : styles.heroVisual}`}
    data-playing={visible && !paused}
    aria-label={converge ? "Research motion study: separate particle rings gather into one Twin." : "A monochrome variation of the Twin’s concentric particle rings."}>
    <svg viewBox={converge ? "0 0 1000 600" : "265 65 470 470"} aria-hidden="true">
      {STORY_LAYERS.map((layer, i) => <g key={layer.color} className={styles.particle}
        style={{ opacity: .9 - i * .085, "--orbit-duration": `${70 + i * 11}s` } as CSSProperties}>
        <path d={layer.held} fill="currentColor" />
      </g>)}
    </svg>
    <button type="button" className={styles.motionButton} onClick={() => setPaused(value => !value)}
      aria-label={paused ? "Play Twin animation" : "Pause Twin animation"}>
      {paused ? <Play size={15} /> : <Pause size={15} />}
    </button>
  </figure>;
}

export function ResearchTwin() { return <TwinAnimation />; }
export function ModelBoundary() { return <TwinAnimation converge />; }

export function UkResearchStack() {
  return <figure className={styles.panel} aria-label="Proposed UK boundary containing server and storage, the Twin and a privately deployed model. Source systems remain where they are.">
    <div className={styles.sourceStrip}><Database size={17} /><span>Your existing systems</span><span>Read-only connections</span></div>
    <div className={styles.stackConnector}><ArrowDown size={19} aria-hidden="true" /></div>
    <div className={styles.stackBoundary}>
      <div className={styles.stackHeader}>
        <svg viewBox="0 0 100 66.67" width={43} height={29} role="img" aria-label="United Kingdom"><UkFlag x={0} y={0} width={100} height={66.67} /></svg>
        <div><strong>Together in the UK</strong><span>Proposed UK deployment</span></div>
        <span className={styles.statusBadge}>In development</span>
      </div>
      <div className={styles.stackRow}><Server size={22} /><div><strong>Server & storage</strong><span>A dedicated home for your Twin</span></div><span>UK datacentre</span></div>
      <div className={styles.stackRow}><TwinMark /><div><strong>Your business Twin</strong><span>Records, definitions and sources</span></div><span>Business knowledge</span></div>
      <div className={styles.stackRow}><Cpu size={22} /><div><strong>Private model</strong><span>Working beside the Twin</span></div><span>UK GPUs</span></div>
      <div className={styles.stackFooter}><LockKeyhole size={15} />Business information and model processing in one UK deployment.</div>
    </div>
    <figcaption className={styles.note}>Server, storage and private model in one UK boundary. Not yet a standard deployment.</figcaption>
  </figure>;
}
